// src/components/empleado/SaldoVacacionesWidget.jsx
import { useEffect, useState } from "react";
import { getBalance } from "../../services/ausencias";
import { getAccessToken } from "../../services/api";

const LABELS = {
  VACACIONES: "Vacaciones",
  LIBRE_DISPOSICION: "Libre disposición",
  ASUNTOS_PROPIOS: "Asuntos propios",
};


function clamp(n, min = 0, max = 100) {
  const v = Number.isFinite(+n) ? +n : 0;
  return Math.min(max, Math.max(min, v));
}

function num(v) {
  return Number.isFinite(+v) ? +v : 0;
}

// Formatea días: permite elegir decimales y si queremos quitar los ceros finales
function fmtDias(dias, { decimals = 2, stripZeros = false } = {}) {
  const v = num(dias);
  const f = v.toFixed(decimals);
  if (!stripZeros) return f.replace(".", ",");
  // quitar ceros y coma si no hace falta
  return f.replace(/\.?0+$/, "").replace(".", ",");
}

// Formatea solo para vacaciones: sin decimales
function fmtDiasVacaciones(dias) {
  const v = Math.round(num(dias));
  return v.toString().replace(".", ",");
}

// Formatea horas a partir de días usando la jornada diaria del usuario
function fmtHorasDesdeDias(dias, jornadaDiariaHoras = 8) {
  const base = num(jornadaDiariaHoras) > 0 ? num(jornadaDiariaHoras) : 8;
  const horas = num(dias) * base;
  const hRed = Math.round(horas);
  return `${hRed} h`;
}

function barTone(pct) {
  if (pct >= 80) return "bg-rose-600";
  if (pct >= 50) return "bg-amber-500";
  return "bg-emerald-600";
}

export default function SaldoVacacionesWidget({
  tipos = ["VACACIONES", "LIBRE_DISPOSICION"],
}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const token = getAccessToken();
      const d = await getBalance(token);
      setData(d || null);
    } catch (e) {
      setErr(e?.message || "Error");
      setData(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (err) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 p-3 text-sm">
        Error cargando saldos: {err}
        <button
          onClick={load}
          className="ml-2 inline-flex items-center px-2 py-1 rounded-md bg-white/80 border border-rose-200 hover:bg-white text-rose-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-4 w-32 bg-slate-200/60 rounded" />
        {[...Array(2)].map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-white/60 bg-white/70 p-3 shadow-sm"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="h-4 w-28 bg-slate-200/70 rounded" />
              <div className="h-3 w-24 bg-slate-200/70 rounded" />
            </div>
            <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden">
              <div className="h-2 w-1/3 animate-pulse bg-slate-300" />
            </div>
            <div className="mt-1 h-3 w-40 bg-slate-200/70 rounded" />
          </div>
        ))}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white/60 p-3 text-sm">
        No hay datos de saldos disponibles.
      </div>
    );
  }

  // Aseguramos array y tipos válidos
  const saldosRaw = Array.isArray(data?.saldos) ? data.saldos : [];
  const jornadaDiariaHoras =
    num(data?.jornada_diaria_horas) > 0 ? num(data.jornada_diaria_horas) : 8;
  const order = new Map(tipos.map((t, i) => [t, i]));
  const saldos = saldosRaw
    .filter((s) => s && typeof s.tipo === "string" && order.has(s.tipo))
    .sort((a, b) => (order.get(a.tipo) ?? 999) - (order.get(b.tipo) ?? 999));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-800">
          Mis saldos {data?.anio ?? ""}
        </h4>
        <button
          onClick={load}
          className="text-xs px-2 py-1 rounded-md bg-white/70 hover:bg-white border border-slate-200 text-slate-700"
          title="Refrescar saldos"
        >
          ⟳ Refrescar
        </button>
      </div>

      {saldos.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white/60 p-3 text-sm">
          No tienes saldos para{" "}
          {tipos.map((t) => LABELS[t] || t).join(",")}
          {data?.anio ? ` en ${data.anio}` : ""}.
        </div>
      ) : (
        saldos.map((s) => {
          const asignado = num(s.asignado);
          const arrastre = num(s.arrastre);
          const gastado = num(s.gastado);
          const total = asignado + arrastre;
          const disponibleCalc = Number.isFinite(+s.disponible)
            ? +s.disponible
            : Math.max(0, total - gastado);

          // Oculta tarjetas inexistentes en el convenio (0 / 0)
          if (s.tipo !== "VACACIONES" && total === 0 && disponibleCalc === 0) {
            return null;
          }

          const pct = total > 0 ? clamp((gastado / total) * 100) : 0;
          const tone = barTone(pct);
          const label = LABELS[s.tipo] || s.tipo.replaceAll("_", " ");

          const isVacaciones = s.tipo === "VACACIONES";

          // Días formateados
          const gastadoDiasStr = isVacaciones
            ? fmtDiasVacaciones(gastado)
            : fmtDias(gastado, { decimals: 2, stripZeros: true });

          const disponibleDiasStr = isVacaciones
            ? fmtDiasVacaciones(disponibleCalc)
            : fmtDias(disponibleCalc, { decimals: 2, stripZeros: true });

          const totalDiasStr = isVacaciones
            ? fmtDiasVacaciones(total)
            : fmtDias(total, { decimals: 2, stripZeros: true });

          // Horas solo para tipos por horas (no vacaciones)
          const gastadoHorasStr = !isVacaciones
            ? fmtHorasDesdeDias(gastado, jornadaDiariaHoras)
            : null;
          const disponibleHorasStr = !isVacaciones
            ? fmtHorasDesdeDias(disponibleCalc, jornadaDiariaHoras)
            : null;
          const totalHorasStr = !isVacaciones
            ? fmtHorasDesdeDias(total, jornadaDiariaHoras)
            : null;

          return (
            <div
              key={s.tipo}
              className="rounded-xl border border-white/60 bg-white/70 p-3 shadow-sm"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm font-medium text-slate-800">
                  {label}
                </div>
                <div className="text-xs text-slate-600">
                  {isVacaciones ? (
                    <>
                      Disponible: <b>{disponibleDiasStr}</b> días / Total:{" "}
                      <b>{totalDiasStr}</b> días
                    </>
                  ) : (
                    <>
                      Disponible: <b>{disponibleDiasStr}</b> días (
                      {disponibleHorasStr}) / Total: <b>{totalDiasStr}</b> días (
                      {totalHorasStr})
                    </>
                  )}
                </div>
              </div>

              <div
                className="h-2 w-full rounded-full bg-slate-200"
                title={
                  isVacaciones
                    ? `Gastado ${gastadoDiasStr} de ${totalDiasStr} días (${Math.round(
                        pct
                      )}%)`
                    : `Gastado ${gastadoDiasStr} días (${gastadoHorasStr}) de ${totalDiasStr} días (${totalHorasStr}) (${Math.round(
                        pct
                      )}%)`
                }
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(pct)}
                aria-label={`Progreso ${label}`}
              >
                <div
                  className={`h-2 rounded-full ${tone} transition-[width] duration-300`}
                  style={{ width: `${clamp(pct)}%` }}
                />
              </div>

              <div className="mt-1 text-[11px] text-slate-600 flex items-center justify-between">
                <span>
                  {isVacaciones ? (
                    <>
                      Gastado: <b>{gastadoDiasStr}</b> días · Disponible:{" "}
                      <b>{disponibleDiasStr}</b> días
                    </>
                  ) : (
                    <>
                      Gastado: <b>{gastadoDiasStr}</b> días (
                      {gastadoHorasStr}) · Disponible:{" "}
                      <b>{disponibleDiasStr}</b> días ({disponibleHorasStr})
                    </>
                  )}
                </span>
                <span>
                  Cómputo:{" "}
                  {(s.computo?.toLowerCase?.() || "laborables")} · Mediodía:{" "}
                  {s.permite_mediodia ? "sí" : "no"}
                </span>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
